<script setup>
definePage({
  name: 'shipment-warehouse-client-groups',
  meta: {
    requiresAuth: true,
    action: 'read',
    subject: 'Parcels',
  },
})

// Define props
const props = defineProps({
  containerId: {
    type: [String, Number],
    default: null
  }
})

import { ref, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'

// Import components
import ClientGroupHeader from './components/ClientGroupHeader.vue'
import ClientGroupCard from './components/ClientGroupCard.vue'
import InspectionDialog from './components/InspectionDialog.vue'
import CounterVerificationDialog from './components/CounterVerificationDialog.vue'
import BatchVerificationDialog from './components/BatchVerificationDialog.vue'
import InsertUnfoundParcelDialog from './components/InsertUnfoundParcelDialog.vue'
import PrintPreviewModal from '@/components/shipment/PrintPreviewModal.vue'


const { t } = useI18n()
const { showSnackbar } = useSnackbar()
const route = useRoute()
const router = useRouter()

const containerId = computed(() => props.containerId || route.params.containerId)
const container = ref(null)
const clientGroups = ref([])
const isLoading = ref(false)
const isSubmitting = ref(false)
const isImportingDimensions = ref(false)
const dimensionImportInput = ref(null)

// Search and pagination
const searchQuery = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)
const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value))

// Client-side filters (team code + workflow status). Applied on top of
// the already-paginated payload — they reduce what's visible without
// hitting the API. `null` = no filter for that dimension.
const filterTeamCode = ref(null)
const filterWorkflowStatus = ref(null)

// Workflow statuses with their display colors. Mirrors WORKFLOW_STATES
// in ClientGroupCard.vue so the filter chips look identical to the row
// pills (same color, same label).
const WORKFLOW_FILTER_STATES = [
  { key: 'EN_COURS',        label: 'EN COURS',        color: '#6B7280' },
  { key: 'MP_ENVOYER',      label: 'MP ENVOYER',      color: '#CA8A04' },
  { key: 'PAYER',           label: 'PAYER',           color: '#B91C1C' },
  { key: 'PAYER_ET_LIVRER', label: 'PAYER ET LIVRER', color: '#16A34A' },
]

// Team chip colors — must match ClientGroupCard.vue + mobile parcel_card.dart.
const TEAM_FILTER_COLORS = {
  GB: '#16A34A',
  GD: '#2563EB',
  JP: '#EA580C',
  MH: '#9333EA',
  QS: '#0D9488',
}

// Distinct team codes that appear in the loaded groups (so we don't
// show a "QS" chip when no QS group is on this container).
const availableTeamCodes = computed(() => {
  const codes = new Set()
  for (const g of clientGroups.value) {
    const c = (g.team_code || '').trim()
    if (c) codes.add(c)
  }
  return Array.from(codes).sort()
})

// Final list rendered on the page after client-side filters.
const visibleClientGroups = computed(() => {
  return clientGroups.value.filter(g => {
    if (filterTeamCode.value && (g.team_code || '').trim() !== filterTeamCode.value) {
      return false
    }
    if (filterWorkflowStatus.value) {
      const ws = g.workflow_status || 'EN_COURS'
      if (ws !== filterWorkflowStatus.value) return false
    }
    return true
  })
})

const hasActiveFilter = computed(
  () => !!filterTeamCode.value || !!filterWorkflowStatus.value
)

const toggleTeamFilter = (code) => {
  filterTeamCode.value = filterTeamCode.value === code ? null : code
}
const toggleWorkflowFilter = (key) => {
  filterWorkflowStatus.value = filterWorkflowStatus.value === key ? null : key
}
const clearFilters = () => {
  filterTeamCode.value = null
  filterWorkflowStatus.value = null
}

const EXCEL_COLUMN_INDEX = {
  trackingNumber: 7,
  length: 17,
  width: 18,
  height: 19,
}

// Mirror the backend barcode cleanup used by packing-list imports so the
// Excel tracking column matches the stored parcel barcode format.
const normalizeTrackingNumber = value => {
  if (value === null || value === undefined) return null

  let trackingNumber = String(value).trim().replace(/^['"`]+|['"`]+$/g, '')

  if (!trackingNumber) return null
  if (trackingNumber.startsWith('=')) return null
  if (trackingNumber.includes('--')) return null

  trackingNumber = trackingNumber.replace(
    /\s+(\d{1,2})\s+(?:0?[789]|1[0-9]):[0-5]\d\s+\d{4}\/\d{1,2}\/(\d{1,2})\s*$/u,
    '',
  ).trim()

  const gluedDayMatch = trackingNumber.match(
    /(\d{1,2})\s+(?:0?[789]|1[0-9]):[0-5]\d\s+\d{4}\/\d{1,2}\/(\d{1,2})\s*$/u,
  )

  if (gluedDayMatch) {
    const [, tailDayRaw, dateDayRaw] = gluedDayMatch
    const tailDay = String(Number.parseInt(tailDayRaw, 10))
    const dateDay = String(Number.parseInt(dateDayRaw, 10))

    if (tailDay === dateDay) {
      trackingNumber = trackingNumber.slice(0, gluedDayMatch.index).trim()
    }
  }

  trackingNumber = trackingNumber.replace(
    /\s+(?:0?[789]|1[0-9]):[0-5]\d(?:\s*\d{4}\/\d{1,2}\/\d{1,2})?\s*$/u,
    '',
  ).trim()

  trackingNumber = trackingNumber.replace(
    /(1[0-9]):([0-5]\d)\s*(?:\d{4}\/\d{1,2}\/\d{1,2})?\s*$/u,
    '',
  ).trim()
  trackingNumber = trackingNumber.replace(
    /([89]):([0-5]\d)\s*(?:\d{4}\/\d{1,2}\/\d{1,2})?\s*$/u,
    '',
  ).trim()

  if (/\s/u.test(trackingNumber)) {
    const tokens = trackingNumber.split(/\s+/u)
    const firstToken = tokens[0] || ''

    trackingNumber = firstToken.length >= 4 && /\d/u.test(firstToken)
      ? firstToken
      : tokens.join('')
  }

  trackingNumber = trackingNumber.trim().toUpperCase()

  return trackingNumber && /\d/u.test(trackingNumber)
    ? trackingNumber
    : null
}

const parseDimensionValue = value => {
  const normalized = String(value ?? '')
    .trim()
    .replace(',', '.')

  if (!normalized) return null

  const parsed = Number(normalized)

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

// TEST COTATION layouts drift between shipments (e.g. 8.7.2026 has an extra
// blank column before TRACKING, and the app's own "Export TEST COTATION" uses
// that newer layout), so locate the columns from the header row: TRACKING by
// name, and the measured L / l / H around the lowercase "l" header (the PL
// dims use "L / I / H"). Falls back to the historical fixed positions.
const resolveExcelColumns = headerRow => {
  const headers = (headerRow || []).map(cell => String(cell ?? '').trim())
  const trackingNumber = headers.findIndex(h => h.toUpperCase() === 'TRACKING')
  const width = headers.indexOf('l')

  if (trackingNumber < 0 || width < 1) return EXCEL_COLUMN_INDEX

  return { trackingNumber, length: width - 1, width, height: width + 1 }
}

const buildDimensionsMapFromRows = rows => {
  const dimensionMap = new Map()
  const columns = resolveExcelColumns(rows[0])

  rows.slice(1).forEach(row => {
    const trackingNumber = normalizeTrackingNumber(row?.[columns.trackingNumber])
    const length = parseDimensionValue(row?.[columns.length])
    const width = parseDimensionValue(row?.[columns.width])
    const height = parseDimensionValue(row?.[columns.height])

    if (!trackingNumber || length === null || width === null || height === null) return

    dimensionMap.set(trackingNumber, { length, width, height })
  })

  return dimensionMap
}

const getAllContainerInspections = async () => {
  const response = await axios.get(`shipment/container-arrivals/${containerId.value}`)
  const allClientGroups = response.data?.data?.client_groups

  if (!Array.isArray(allClientGroups)) return []

  return allClientGroups.flatMap(group => Array.isArray(group.inspections) ? group.inspections : [])
}

const openDimensionImportPicker = () => {
  if (isImportingDimensions.value) return

  dimensionImportInput.value?.click()
}

const importDimensionsFromExcel = async event => {
  const file = event.target?.files?.[0]

  if (event.target) event.target.value = ''
  if (!file) return

  isImportingDimensions.value = true

  try {
    const XLSX = await import('xlsx')
    const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' })
    const sheetName = workbook.SheetNames.includes('TEST COTATION')
      ? 'TEST COTATION'
      : workbook.SheetNames[0]
    const worksheet = workbook.Sheets[sheetName]

    if (!worksheet) {
      showSnackbar({
        color: 'error',
        text: 'Impossible de lire la feuille Excel.',
      })

      return
    }

    const rows = XLSX.utils.sheet_to_json(worksheet, {
      header: 1,
      raw: false,
      defval: '',
    })

    const dimensionMap = buildDimensionsMapFromRows(rows)

    if (dimensionMap.size === 0) {
      showSnackbar({
        color: 'warning',
        text: "Aucune ligne valide avec tracking et dimensions n'a ete trouvee.",
      })

      return
    }

    const inspections = await getAllContainerInspections()

    if (inspections.length === 0) {
      showSnackbar({
        color: 'info',
        text: 'Aucun parcel de ce packing list a mettre a jour.',
      })

      return
    }

    let matchedCount = 0
    let skippedAlreadyUpdatedCount = 0
    let updatedCount = 0
    let failedCount = 0

    for (const inspection of inspections) {
      const trackingNumber = normalizeTrackingNumber(inspection.incoming_parcel?.barcode)

      if (!trackingNumber) continue

      const dimensions = dimensionMap.get(trackingNumber)

      if (!dimensions) continue

      matchedCount++

      if (inspection.inspected_at) {
        skippedAlreadyUpdatedCount++
        continue
      }

      try {
        await axios.put(`shipment/warehouse/inspections/${inspection.id}`, {
          arrival_length: dimensions.length,
          arrival_width: dimensions.width,
          arrival_height: dimensions.height,
        })
        updatedCount++
      } catch (error) {
        failedCount++
        console.error('Error importing parcel dimensions:', {
          inspectionId: inspection.id,
          trackingNumber,
          error,
        })
      }
    }

    if (updatedCount > 0) {
      await fetchContainer()
    }

    if (matchedCount === 0) {
      showSnackbar({
        color: 'info',
        text: 'Aucun tracking du fichier ne correspond aux parcels de ce packing list.',
      })

      return
    }

    const summary = [
      `${updatedCount} parcel(s) mis a jour`,
      `${skippedAlreadyUpdatedCount} deja mesures`,
    ]

    if (failedCount > 0) {
      summary.push(`${failedCount} echec(s)`)
    }

    showSnackbar({
      color: failedCount > 0 ? 'warning' : 'success',
      text: summary.join(' | '),
    })
  } catch (error) {
    console.error('Error importing Excel dimensions:', error)
    showSnackbar({
      color: 'error',
      text: "Echec de l'import Excel.",
    })
  } finally {
    isImportingDimensions.value = false
  }
}

// Dialog controls
const inspectionDialog = ref(false)
const counterVerificationDialog = ref(false)
const batchVerificationDialog = ref(false)
const currentInspection = ref(null)
const selectedClientGroup = ref(null)
const allInspectionsInBatch = ref([])
const currentBatchIndex = ref(0)

const showPrintPreview = ref(false)
const currentClientGroupForPrint = ref(null)

// Merge mode + selection state
const mergeMode = ref(false)
const selectedForMerge = ref([])  // group ids
const mergeDialog = ref(false)
const mergeTargetId = ref(null)
const mergeBusy = ref(false)

const selectedGroupObjects = computed(() =>
  clientGroups.value.filter(g => selectedForMerge.value.includes(g.id))
)

const toggleMergeMode = () => {
  mergeMode.value = !mergeMode.value
  if (!mergeMode.value) {
    selectedForMerge.value = []
    mergeTargetId.value = null
  }
}

const onToggleMergeSelect = id => {
  const i = selectedForMerge.value.indexOf(id)
  if (i === -1) selectedForMerge.value.push(id)
  else selectedForMerge.value.splice(i, 1)
}

const openMergeDialog = () => {
  if (selectedForMerge.value.length < 2) {
    showSnackbar({ color: 'warning', text: t('shipment.merge_groups_select_at_least_two') })
    return
  }
  mergeTargetId.value = selectedForMerge.value[0]
  mergeDialog.value = true
}

const submitMerge = async () => {
  if (!mergeTargetId.value) {
    showSnackbar({ color: 'warning', text: t('shipment.merge_groups_select_target') })
    return
  }
  const sources = selectedForMerge.value.filter(id => id !== mergeTargetId.value)
  if (sources.length < 1) {
    showSnackbar({ color: 'warning', text: t('shipment.merge_groups_select_at_least_two') })
    return
  }
  mergeBusy.value = true
  try {
    await axios.post('shipment/warehouse/groups/merge', {
      source_group_ids: sources,
      target_group_id: mergeTargetId.value,
    })
    showSnackbar({ color: 'success', text: t('shipment.groups_merged') })
    mergeDialog.value = false
    mergeMode.value = false
    selectedForMerge.value = []
    mergeTargetId.value = null
    await fetchContainer()
  } catch (error) {
    console.error('Error merging groups:', error)
    const status = error.response?.status
    const msg = error.response?.data?.message
      || (status === 409 ? t('shipment.merge_blocked_by_invoice') : t('messages.something_went_wrong'))
    showSnackbar({ color: 'error', text: msg })
  } finally {
    mergeBusy.value = false
  }
}

// Move-parcel: triggered from inside a card. Just refresh container after success.
const onParcelMoved = async () => {
  await fetchContainer()
}

// Watch for search query or pagination changes
watch([searchQuery, currentPage, itemsPerPage], () => {
  fetchContainer()
}, { deep: true })

// Fetch container details
const fetchContainer = async () => {
  try {
    isLoading.value = true
    
    // Include search and pagination parameters
    const params = {}
    
    // Only add non-empty parameters
    if (searchQuery.value) params.search = searchQuery.value
    if (currentPage.value) params.page = currentPage.value
    if (itemsPerPage.value) params.per_page = itemsPerPage.value
    
    const response = await axios.get(`shipment/container-arrivals/${containerId.value}`, { params })

    container.value = response.data.data
    
    // Check if client_groups exist and have inspections
    if (container.value && container.value.client_groups) {
      clientGroups.value = container.value.client_groups
      
      // Update pagination info if available in response
      if (response.data.meta) {
        totalItems.value = response.data.meta.total || clientGroups.value.length
        currentPage.value = response.data.meta.current_page || 1
      } else {
        totalItems.value = clientGroups.value.length
      }
      
      // Debug information
      if (clientGroups.value.length > 0) {

      }
    }
  } catch (error) {
    console.error('Error fetching container:', error)
    showSnackbar({
      color: 'error',
      text: t('messages.something_went_wrong'),
    })
  } finally {
    isLoading.value = false
  }
}

// Handle page change
const handlePageChange = page => {
  currentPage.value = page
}

// Handle per page change
const handleItemsPerPageChange = perPage => {
  itemsPerPage.value = perPage
  currentPage.value = 1 // Reset to first page when changing items per page
}

// Open inspection dialog
const openInspectionDialog = inspection => {
  currentInspection.value = inspection
  inspectionDialog.value = true
}

// Open counter verification dialog
const openCounterVerificationDialog = inspection => {
  // Check if inspection has been completed
  if (!inspection.inspected_at) {
    showSnackbar({
      color: 'warning',
      text: t('shipment.initial_inspection_required'),
    })
    
    return
  }
  
  currentInspection.value = inspection
  counterVerificationDialog.value = true
}

// Open batch verification dialog
const openBatchVerificationDialog = clientGroup => {
  // Check if the inspection is complete
  if (!clientGroup.inspection_completed_at) {
    showSnackbar({
      color: 'warning',
      text: t('shipment.inspection_not_completed'),
    })
    
    return
  }
  
  // Filter inspections that need counter verification
  const inspections = clientGroup.inspections.filter(
    inspection => inspection.inspected_at && !inspection.counter_verified_at,
  )
  
  if (inspections.length === 0) {
    showSnackbar({
      color: 'info',
      text: t('shipment.all_parcels_verified'),
    })
    
    return
  }
  
  selectedClientGroup.value = clientGroup
  allInspectionsInBatch.value = inspections
  currentBatchIndex.value = 0
  currentInspection.value = inspections[0]
  batchVerificationDialog.value = true
}

// Complete inspection
const completeInspection = async clientGroupId => {
  isSubmitting.value = true
  try {
    await axios.post(`shipment/warehouse/complete-inspection/${clientGroupId}`)
    
    showSnackbar({
      color: 'success',
      text: t('shipment.inspection_completed_success'),
    })
    
    // Refresh data
    fetchContainer()
  } catch (error) {
    console.error('Error completing inspection:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// Complete counter verification
const completeCounterVerification = async clientGroupId => {
  isSubmitting.value = true
  try {
    await axios.post(`shipment/warehouse/complete-counter-verification/${clientGroupId}`)
    
    showSnackbar({
      color: 'success',
      text: t('shipment.counter_verification_completed'),
    })
    
    // Refresh data
    fetchContainer()
  } catch (error) {
    console.error('Error completing counter verification:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// Mark eligible parcels ready_for_pickup (customer-facing status flip).
// The card emits the list of parcel_ids already at counter_verified;
// we POST to the existing endpoint which:
//   - flips each parcel's status to ready_for_pickup
//   - skips any not currently at counter_verified (defensive)
//   - sends SMS to opted-in customers
const markReadyForPickup = async ({ groupId, parcelIds }) => {
  if (!Array.isArray(parcelIds) || parcelIds.length === 0) return
  isSubmitting.value = true
  try {
    // The route is registered under the Shipment module which prefixes
    // everything with /api/shipment — so the call must be
    // `shipment/parcels/...`, not `parcels/...` (axios baseURL is /api).
    const { data } = await axios.post('shipment/parcels/mark-as-ready-for-pickup', {
      parcel_ids: parcelIds,
    })
    const updatedCount = data?.data?.updated_count ?? parcelIds.length
    const skippedCount = data?.data?.skipped_count ?? 0
    showSnackbar({
      color: 'success',
      text: skippedCount > 0
        ? t('shipment.ready_for_pickup_partial', { n: updatedCount, skipped: skippedCount })
        : t('shipment.ready_for_pickup_done', { n: updatedCount }),
    })
    fetchContainer()
  } catch (error) {
    console.error('Error marking ready for pickup:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// Generate invoice
const generateInvoice = async clientGroupId => {
  isSubmitting.value = true
  try {
    const response = await axios.post(`shipment/warehouse/generate-invoice/${clientGroupId}`)

    showSnackbar({
      color: 'success',
      text: t('shipment.invoice_generated'),
    })

    // Navigate to invoice view
    router.push({
      name: 'shipment-warehouse-invoices-view',
      params: { invoiceId: response.data.data.id },
    })
  } catch (error) {
    console.error('Error generating invoice:', error)
    showSnackbar({
      color: 'error',
      text: error.response?.data?.message || t('messages.something_went_wrong'),
    })
  } finally {
    isSubmitting.value = false
  }
}

// Cancel invoice — superadmin-only on the backend. Asks the user to
// confirm + optionally type a reason that's persisted in the audit log.
// On success, refresh the container so the group's `hasInvoice` resets.
const cancelInvoiceCtx = ref(null)        // { invoiceId, groupId, groupRef, externalNumber }
const cancelInvoiceReason = ref('')
const cancelInvoiceForce = ref(false)
const cancelInvoiceLoading = ref(false)

const onCancelInvoiceRequested = ctx => {
  cancelInvoiceCtx.value = ctx
  cancelInvoiceReason.value = ''
  cancelInvoiceForce.value = false
}

const closeCancelInvoiceDialog = () => {
  cancelInvoiceCtx.value = null
  cancelInvoiceReason.value = ''
  cancelInvoiceForce.value = false
}

const confirmCancelInvoice = async () => {
  const ctx = cancelInvoiceCtx.value
  if (!ctx) return
  cancelInvoiceLoading.value = true
  try {
    const params = cancelInvoiceForce.value ? { force: 1 } : {}
    await axios.delete(`shipment/warehouse/invoices/${ctx.invoiceId}`, {
      params,
      data: { reason: cancelInvoiceReason.value || undefined },
    })
    showSnackbar({
      color: 'success',
      text: t('shipment.invoice_cancelled'),
    })
    closeCancelInvoiceDialog()
    await fetchContainerDetails()  // reload so the group's invoice state is fresh
  } catch (error) {
    console.error('Error cancelling invoice:', error)
    const code = error.response?.data?.error_code
    if (code === 'INVOICE_HAS_EXTERNAL_NUMBER' && !cancelInvoiceForce.value) {
      // Surface the option to force; keep dialog open.
      cancelInvoiceForce.value = true
      showSnackbar({
        color: 'warning',
        text: error.response?.data?.message || t('shipment.invoice_cancel_blocked_by_external'),
      })
    } else {
      showSnackbar({
        color: 'error',
        text: error.response?.data?.message || t('messages.something_went_wrong'),
      })
    }
  } finally {
    cancelInvoiceLoading.value = false
  }
}

const printClientGroup = clientGroup => {
  currentClientGroupForPrint.value = clientGroup
  showPrintPreview.value = true
}


// NOUVEAU : Configuration des données d'impression
const printClientGroupData = computed(() => {
  if (!currentClientGroupForPrint.value || !currentClientGroupForPrint.value.inspections) return []
  
  return currentClientGroupForPrint.value.inspections.map(inspection => ({
    id: inspection.id,
    client_group_id: currentClientGroupForPrint.value.id,
    customer_name: currentClientGroupForPrint.value.customer?.name || '-',
    customer_email: currentClientGroupForPrint.value.customer?.email || '-',
    customer_phone: currentClientGroupForPrint.value.customer?.phone || '-',
    parcel_barcode: inspection.incoming_parcel?.barcode || '-',
    parcel_name: inspection.incoming_parcel?.name || '-',
    parcel_description: inspection.incoming_parcel?.description || '-',
    arrival_weight: inspection.arrival_weight,
    arrival_length: inspection.arrival_length,
    arrival_width: inspection.arrival_width,
    arrival_height: inspection.arrival_height,
    arrival_volume: inspection.arrival_volume,
    volumetric_weight: inspection.volumetric_weight,
    inspection_status: inspection.status || 'pending',
    inspected_at: inspection.inspected_at,
    counter_verified_at: inspection.counter_verified_at,
    created_at: currentClientGroupForPrint.value.created_at,
    invoice_number: currentClientGroupForPrint.value.shipping_transaction?.invoice_number || '-',
  }))
})

// NOUVEAU : Configuration des colonnes d'impression
const printTableColumns = computed(() => [
  { key: 'index', title: '#', value: 'index' },
  { key: 'parcel_barcode', title: t('shipment.parcel_barcode'), value: 'parcel_barcode' },
  { key: 'parcel_name', title: t('shipment.parcel_name'), value: 'parcel_name' },
  { key: 'description', title: t('shipment.description'), value: 'parcel_description' },
  { key: 'weight', title: t('shipment.weight'), value: 'arrival_weight', format: 'weight' },
  { key: 'dimensions', title: t('shipment.dimensions'), value: 'dimensions', format: 'dimensions' },
  { key: 'volume', title: t('shipment.volume'), value: 'arrival_volume' },
  { key: 'volumetric_weight', title: t('shipment.volumetric_weight'), value: 'volumetric_weight', format: 'weight' },
  { key: 'status', title: t('shipment.status'), value: 'inspection_status' },
  { key: 'inspected_at', title: t('shipment.inspected_at'), value: 'inspected_at', format: 'date' },
  { key: 'counter_verified_at', title: t('shipment.counter_verified_at'), value: 'counter_verified_at', format: 'date' },
])

// NOUVEAU : Titre d'impression dynamique
const printTitle = computed(() => {
  if (!currentClientGroupForPrint.value) return t('actions.print')
  
  const customerName = currentClientGroupForPrint.value.customer?.name || 'Client'
  const inspectionCount = currentClientGroupForPrint.value.inspections?.length || 0
  
  return `${t('shipment.client_group')} - ${customerName} (${inspectionCount} ${t('shipment.inspections')})`
})

// NOUVEAU : Totaux personnalisés pour Client Groups
const clientGroupCustomTotals = computed(() => [
  {
    key: 'total_inspections',
    label: t('shipment.total_inspections'),
    field: 'id',
    format: 'integer',
  },
  {
    key: 'completed_inspections',
    label: t('shipment.completed_inspections'),
    field: 'inspected_at',
    format: 'integer',
  },
  {
    key: 'average_volumetric_weight',
    label: t('shipment.average_volumetric_weight'),
    field: 'volumetric_weight',
    format: 'weight',
  },
])

const handlePrint = () => {
  showPrintPreview.value = false
}

// Navigation
const goBack = () => {
  router.push({ name: 'shipment-warehouse' })
}

// Next batch item
const goToNextBatchItem = () => {
  if (currentBatchIndex.value < allInspectionsInBatch.value.length - 1) {
    currentBatchIndex.value++
    currentInspection.value = allInspectionsInBatch.value[currentBatchIndex.value]
  }
}

// Previous batch item
const goToPreviousBatchItem = () => {
  if (currentBatchIndex.value > 0) {
    currentBatchIndex.value--
    currentInspection.value = allInspectionsInBatch.value[currentBatchIndex.value]
  }
}

// "Insert unfound parcel" dialog — bubbled from ClientGroupHeader's
// new button. The dialog itself does the POST; on success we just
// refetch the container so the new inspection/group shows up.
const showInsertUnfoundDialog = ref(false)
const handleUnfoundInserted = () => {
  fetchContainer()
}

// Initialize
onMounted(() => {
  fetchContainer()
})
</script>

<template>
  <div>
    <ClientGroupHeader
      :container="container"
      :search-query="searchQuery"
      :is-importing-dimensions="isImportingDimensions"
      @go-back="goBack"
      @update:search-query="searchQuery = $event; currentPage = 1"
      @insert-unfound="showInsertUnfoundDialog = true"
      @import-dimensions="openDimensionImportPicker"
    />

    <input
      ref="dimensionImportInput"
      type="file"
      accept=".xlsx,.xls"
      style="display: none"
      @change="importDimensionsFromExcel"
    >

    <!-- Insert-unfound-parcel dialog: operator scanned a barcode that has
         no IncomingParcel row. The dialog POSTs to
         /shipment/warehouse/parcels/insert-unfound and we refetch on
         success so the new inspection appears in the right group. -->
    <InsertUnfoundParcelDialog
      v-model="showInsertUnfoundDialog"
      :container="container"
      @inserted="handleUnfoundInserted"
    />
    
    <!-- Status and Pagination Info -->
    <VCard
      class="mb-4"
      outlined
    >
      <VCardText>
        <VRow>
          <VCol
            cols="12"
            md="6"
            class="d-flex align-center gap-2 flex-wrap"
          >
            <VBtn
              :color="mergeMode ? 'warning' : 'secondary'"
              :variant="mergeMode ? 'elevated' : 'tonal'"
              size="small"
              :prepend-icon="mergeMode ? 'tabler-x' : 'tabler-arrow-merge'"
              @click="toggleMergeMode"
            >
              {{ mergeMode ? t('actions.cancel') : t('shipment.merge_into_target') }}
            </VBtn>
            <template v-if="mergeMode">
              <VChip
                size="small"
                color="warning"
                variant="tonal"
              >
                {{ selectedForMerge.length }} {{ t('shipment.client_groups').toLowerCase() }}
              </VChip>
              <VBtn
                color="primary"
                size="small"
                :disabled="selectedForMerge.length < 2"
                prepend-icon="tabler-arrow-merge"
                @click="openMergeDialog"
              >
                {{ t('shipment.merge_into_target') }}…
              </VBtn>
            </template>
          </VCol>
          <VCol
            cols="12"
            md="6"
            class="d-flex justify-end align-center"
          >
            <div class="text-caption text-medium-emphasis">
              {{ t('general.showing') }} {{ visibleClientGroups.length ? (currentPage - 1) * itemsPerPage + 1 : 0 }}
              {{ t('general.to') }}
              {{ Math.min(currentPage * itemsPerPage, totalItems) }}
              {{ t('general.of') }} {{ totalItems }} {{ t('general.entries') }}
              <template v-if="hasActiveFilter">
                · <span class="text-warning">{{ t('general.filtered') }}: {{ visibleClientGroups.length }}</span>
              </template>
            </div>
          </VCol>
        </VRow>

        <!-- Team + status filter chips. Mirrors the mobile filter row.
             Team chips only render for teams actually present in the
             container's groups so a worker doesn't get bombarded with
             empty filter options. -->
        <VRow
          v-if="availableTeamCodes.length > 0 || clientGroups.length > 0"
          class="mt-2"
        >
          <VCol
            cols="12"
            class="d-flex align-center gap-2 flex-wrap"
          >
            <span class="text-caption text-medium-emphasis me-2">
              {{ t('general.filter') }}:
            </span>
            <VChip
              v-for="code in availableTeamCodes"
              :key="`team-${code}`"
              size="small"
              :variant="filterTeamCode === code ? 'flat' : 'outlined'"
              :color="TEAM_FILTER_COLORS[code] || '#6B7280'"
              :style="filterTeamCode === code ? { color: '#fff', backgroundColor: TEAM_FILTER_COLORS[code] || '#6B7280' } : { color: TEAM_FILTER_COLORS[code] || '#6B7280' }"
              class="font-weight-bold"
              style="letter-spacing: 0.5px;"
              @click="toggleTeamFilter(code)"
            >
              {{ code }}
            </VChip>
            <VDivider vertical class="mx-2" />
            <VChip
              v-for="ws in WORKFLOW_FILTER_STATES"
              :key="`ws-${ws.key}`"
              size="small"
              :variant="filterWorkflowStatus === ws.key ? 'flat' : 'outlined'"
              :color="ws.color"
              :style="filterWorkflowStatus === ws.key ? { color: '#fff', backgroundColor: ws.color } : { color: ws.color }"
              class="font-weight-bold"
              style="letter-spacing: 0.5px;"
              @click="toggleWorkflowFilter(ws.key)"
            >
              {{ ws.label }}
            </VChip>
            <VBtn
              v-if="hasActiveFilter"
              size="x-small"
              variant="text"
              prepend-icon="tabler-x"
              @click="clearFilters"
            >
              {{ t('general.clear') }}
            </VBtn>
          </VCol>
        </VRow>
      </VCardText>
    </VCard>
    
    <VProgressLinear
      v-if="isLoading"
      indeterminate
    />
    
    <VAlert
      v-else-if="clientGroups.length === 0"
      type="info"
      variant="tonal"
      class="my-4"
      prepend-icon="tabler-info-circle"
    >
      <div class="text-body-1 font-weight-medium mb-1">
        {{ t('shipment.client_groups_empty_title') }}
      </div>
      <div class="text-body-2 mb-3">
        {{ t('shipment.client_groups_empty_hint') }}
      </div>
      <VBtn
        color="primary"
        size="small"
        variant="tonal"
        prepend-icon="tabler-arrows-shuffle"
        :to="`/shipment/warehouse/arrange-parcels/${containerId}`"
      >
        {{ t('shipment.client_groups_empty_action') }}
      </VBtn>
    </VAlert>
    
    <div v-else>
      <VAlert
        v-if="hasActiveFilter && visibleClientGroups.length === 0"
        type="info"
        variant="tonal"
        class="mb-4"
        prepend-icon="tabler-filter"
      >
        {{ t('shipment.no_groups_match_filter') || 'Aucun groupe ne correspond au filtre.' }}
      </VAlert>
      <ClientGroupCard
        v-for="clientGroup in visibleClientGroups"
        :key="clientGroup.id"
        :client-group="clientGroup"
        :container-status="container?.status"
        :is-submitting="isSubmitting"
        :selectable-for-merge="mergeMode"
        :selected-for-merge="selectedForMerge.includes(clientGroup.id)"
        :other-groups="clientGroups.filter(g => g.id !== clientGroup.id)"
        @open-inspection="openInspectionDialog"
        @open-counter-verification="openCounterVerificationDialog"
        @open-batch-verification="openBatchVerificationDialog"
        @complete-inspection="completeInspection"
        @complete-counter-verification="completeCounterVerification"
        @generate-invoice="generateInvoice"
        @cancel-invoice="onCancelInvoiceRequested"
        @print-client-group="printClientGroup"
        @toggle-merge-select="onToggleMergeSelect"
        @parcel-moved="onParcelMoved"
        @mark-ready-for-pickup="markReadyForPickup"
      />
      
      <!-- Pagination and Items Per Page -->
      <div class="mt-6">
        <VRow>
          <VCol
            cols="12"
            md="8"
            class="d-flex justify-center align-center"
          >
            <VPagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="5"
              density="comfortable"
              rounded
              @update:model-value="handlePageChange"
            />
          </VCol>
          <VCol
            cols="12"
            md="4"
            class="d-flex justify-end align-center"
          >
            <VSelect
              v-model="itemsPerPage"
              :items="[5, 10, 15, 25, 50]"
              :label="t('general.items_per_page')"
              density="compact"
              variant="outlined"
              hide-details
              class="items-per-page-select"
              style="max-width: 150px"
              @update:model-value="handleItemsPerPageChange"
            />
          </VCol>
        </VRow>
      </div>
    </div>
    
    <InspectionDialog
      v-model="inspectionDialog"
      :inspection="currentInspection"
      @updated="fetchContainer"
    />
    
    <CounterVerificationDialog
      v-model="counterVerificationDialog"
      :inspection="currentInspection"
      @updated="fetchContainer"
    />
    
    <BatchVerificationDialog
      v-model="batchVerificationDialog"
      :inspection="currentInspection"
      :batch-index="currentBatchIndex"
      :batch-total="allInspectionsInBatch.length"
      @next="goToNextBatchItem"
      @previous="goToPreviousBatchItem"
      @updated="fetchContainer"
    />

    <!-- Merge confirmation dialog -->
    <VDialog
      v-model="mergeDialog"
      max-width="560"
      persistent
    >
      <VCard>
        <VCardTitle>{{ t('shipment.merge_groups_dialog_title') }}</VCardTitle>
        <VCardText>
          <p class="mb-3">
            {{ t('shipment.merging_n_groups_into', { n: selectedForMerge.length }) }}
          </p>
          <VSelect
            v-model="mergeTargetId"
            :items="selectedGroupObjects.map(g => ({ value: g.id, title: `#${g.id} — ${g.customer?.name || '?'} (${g.inspections?.length || 0} parcels)` }))"
            :label="t('shipment.merge_groups_target_label')"
            variant="outlined"
            density="comfortable"
          />
          <ul class="mt-3 text-caption text-medium-emphasis">
            <li v-for="g in selectedGroupObjects.filter(g => g.id !== mergeTargetId)" :key="g.id">
              #{{ g.id }} — {{ g.customer?.name || '?' }} ({{ g.inspections?.length || 0 }} parcels) → merged
            </li>
          </ul>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            :disabled="mergeBusy"
            @click="mergeDialog = false"
          >
            {{ t('actions.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="mergeBusy"
            prepend-icon="tabler-arrow-merge"
            @click="submitMerge"
          >
            {{ t('shipment.merge_into_target') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>

  <PrintPreviewModal
    v-model:visible="showPrintPreview"
    :title="printTitle"
    :selected-items="printClientGroupData"
    :table-columns="printTableColumns"
    show-summary
    show-total-weight
    show-total-volume
    :custom-totals="clientGroupCustomTotals"
    :weight-fields="['arrival_weight', 'volumetric_weight']"
    :volume-fields="['arrival_volume']"
    :reference-number="`CG-${containerId}-${currentClientGroupForPrint?.customer?.name?.replace(/\s+/g, '-') || 'Client'}-${new Date().getFullYear()}`"
    company-name="Godana Service - Client Groups"
    class="client-group-print-modal"
    @print="handlePrint"
  >
    <template #summaryExtra="{ items, totals }">
      <div class="client-group-print-summary">
        <h5 class="summary-extra-title">
          {{ t('shipment.inspection_summary') }}
        </h5>
        <div class="inspection-stats">
          <div class="inspection-stat success">
            <VIcon
              icon="tabler-circle-check"
              class="stat-icon"
            />
            <div class="stat-content">
              <span class="stat-label">{{ t('shipment.completed_inspections') }}:</span>
              <span class="stat-value">{{ items.filter(item => item.inspected_at).length }}</span>
            </div>
          </div>
            
          <div class="inspection-stat info">
            <VIcon
              icon="tabler-ruler-2"
              class="stat-icon"
            />
            <div class="stat-content">
              <span class="stat-label">{{ t('shipment.counter_verified') }}:</span>
              <span class="stat-value">{{ items.filter(item => item.counter_verified_at).length }}</span>
            </div>
          </div>
            
          <div class="inspection-stat warning">
            <VIcon
              icon="tabler-clock"
              class="stat-icon"
            />
            <div class="stat-content">
              <span class="stat-label">{{ t('shipment.pending_inspections') }}:</span>
              <span class="stat-value">{{ items.filter(item => !item.inspected_at).length }}</span>
            </div>
          </div>
        </div>
          
        <!-- Informations client -->
        <div class="customer-info-section">
          <h6 class="customer-info-title">
            {{ t('shipment.customer_information') }}
          </h6>
          <div class="customer-details">
            <div class="customer-detail">
              <strong>{{ t('shipment.name') }}:</strong> {{ currentClientGroupForPrint?.customer?.name || '-' }}
            </div>
            <div class="customer-detail">
              <strong>{{ t('shipment.email') }}:</strong> {{ currentClientGroupForPrint?.customer?.email || '-' }}
            </div>
            <div class="customer-detail">
              <strong>{{ t('shipment.phone') }}:</strong> {{ currentClientGroupForPrint?.customer?.phone || '-' }}
            </div>
            <div class="customer-detail">
              <strong>{{ t('shipment.invoice') }}:</strong> 
              <VChip 
                :color="currentClientGroupForPrint?.shipping_transaction?.invoice_number ? 'success' : 'warning'"
                variant="tonal"
                size="small"
              >
                {{ currentClientGroupForPrint?.shipping_transaction?.invoice_number || t('shipment.no_invoice') }}
              </VChip>
            </div>
          </div>
        </div>
      </div>
    </template>
  </PrintPreviewModal>

  <!-- Cancel-invoice confirm dialog (superadmin) -->
  <VDialog
    :model-value="!!cancelInvoiceCtx"
    max-width="540"
    persistent
    @update:model-value="(v) => !v && closeCancelInvoiceDialog()"
  >
    <VCard v-if="cancelInvoiceCtx">
      <VCardTitle class="d-flex align-center">
        <VIcon icon="tabler-receipt-off" color="error" class="me-2" />
        {{ t('shipment.cancel_invoice_dialog_title') }}
      </VCardTitle>
      <VCardText>
        <p class="mb-3">
          {{ t('shipment.cancel_invoice_dialog_body') }}
        </p>
        <p class="text-body-2 text-medium-emphasis mb-3">
          <strong>{{ cancelInvoiceCtx.groupRef }}</strong>
          — invoice #{{ cancelInvoiceCtx.invoiceId }}
          <span v-if="cancelInvoiceCtx.externalNumber">
            (ext: {{ cancelInvoiceCtx.externalNumber }})
          </span>
        </p>

        <VAlert
          v-if="cancelInvoiceForce && cancelInvoiceCtx.externalNumber"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ t('shipment.cancel_invoice_force_warning', { num: cancelInvoiceCtx.externalNumber }) }}
        </VAlert>

        <VTextarea
          v-model="cancelInvoiceReason"
          :label="t('shipment.cancel_invoice_reason_label')"
          variant="outlined"
          rows="2"
          density="compact"
          hide-details
        />
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn variant="text" :disabled="cancelInvoiceLoading" @click="closeCancelInvoiceDialog">
          {{ t('common.cancel') }}
        </VBtn>
        <VBtn
          color="error"
          variant="elevated"
          prepend-icon="tabler-receipt-off"
          :loading="cancelInvoiceLoading"
          @click="confirmCancelInvoice"
        >
          {{ t('shipment.cancel_invoice') }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
.inspection-table {
  font-size: 0.875rem;
}

.w-100 {
  width: 100%;
}
</style>
